import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, Cookie, Calendar, Settings, Shield, Eye, Database, Mail, AlertTriangle, Link, Target, Edit } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-300 rounded-lg mb-4 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/10" : "bg-white";
  const borderColor = type === "important" ? "border-[#ab3d22]/30" : "border-gray-300";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6 shadow-sm`}>
      {children}
    </div>
  );
};

const CookiesPage = () => {
  const [openSections, setOpenSections] = useState({
    whatAre: true,
    types: false,
    management: false,
    thirdParty: false,
    rights: false,
    contact: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />
      
      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#d6dbd8]"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-slate-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-zinc-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:min-h-[200px] w-full">
            <div className="text-left flex-1 max-w-lg">
              {/* Main Headline */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                Cookies
                {/* Brand Accent Line */}
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-[#ab3d22] rounded-full"></div>
              </h1>
              
              {/* Refined Tagline */}
              <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light">
                How Radiance uses cookies and tracking technologies
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: December 8, 2025</span>
          </div>

          {/* Introduction */}
          <div className="mb-8 p-4 bg-white border border-gray-300 rounded-lg shadow-sm">
            <p className="text-sm text-gray-600 mb-4">
              Radiance uses cookies and similar technologies to improve your browsing experience, understand how our website is used, and support our affiliate-based business model. This page explains what these technologies do and how you can manage your preferences.
            </p>
            <CalloutBox type="important">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Your privacy matters to us.</p>
                  <p className="text-sm">
                    Radiance does not sell your personal data.
                  </p>
                </div>
              </div>
            </CalloutBox>
          </div>

          {/* 1. What Are Cookies? */}
          <AccordionItem
            title="1. What Are Cookies?"
            isOpen={openSections.whatAre}
            onToggle={() => toggleSection('whatAre')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences and enable features such as analytics, performance tracking, and affiliate link attribution.
              </p>
              
              <div className="flex items-start gap-3">
                <Link className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Pixels & Tags</h4>
                  <p className="text-sm text-gray-600">
                    Radiance may also use pixels, tags, and similar tools that function like cookies. These help us understand site activity and ensure affiliate partners can confirm a purchase originated from our website.
                  </p>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* 2. Types of Cookies We Use */}
          <AccordionItem
            title="2. Types of Cookies We Use"
            isOpen={openSections.types}
            onToggle={() => toggleSection('types')}
          >
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Essential Cookies</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Required for the website to operate safely and smoothly. These cannot be disabled.
                  </p>
                  <p className="text-sm text-gray-700 font-medium mb-1">Used for:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Core site functionality</li>
                    <li>• Security and fraud prevention</li>
                    <li>• Remembering your cookie consent choices</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Analytics Cookies</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Help us understand how people use Radiance so we can improve content and site performance.
                  </p>
                  <p className="text-sm text-gray-700 font-medium mb-1">Used for:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Page view and traffic measurement</li>
                    <li>• Identifying popular content</li>
                    <li>• Detecting technical issues</li>
                    <li>• Improving user experience</li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-2 italic">
                    These cookies provide anonymous, aggregated insights — not personal data.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Functional Cookies</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Make the site more tailored to you.
                  </p>
                  <p className="text-sm text-gray-700 font-medium mb-1">Used for:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Language or display preferences</li>
                    <li>• Newsletter subscription settings</li>
                    <li>• Saving search filters or browsing preferences</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Marketing & Affiliate Cookies</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Used to support our business model and show you relevant content.
                  </p>
                  <p className="text-sm text-gray-700 font-medium mb-1">Used for:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Tracking affiliate link clicks and confirming purchases (anonymous)</li>
                    <li>• Ensuring Radiance can earn a commission at no extra cost to you</li>
                    <li>• Supporting social media integrations and limited retargeting where applicable</li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-2 italic">
                    These cookies do not give us access to your personal identity or purchase details.
                  </p>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* 3. Managing Your Cookie Preferences */}
          <AccordionItem
            title="3. Managing Your Cookie Preferences"
            isOpen={openSections.management}
            onToggle={() => toggleSection('management')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600 font-medium">
                You remain in full control.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Settings className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Browser Controls</h4>
                    <p className="text-sm text-gray-600">
                      Most browsers allow you to block, delete, or limit cookies — including third-party cookies.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Cookie className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Cookie Consent Banner</h4>
                    <p className="text-sm text-gray-600">
                      On your first visit, you may choose which optional cookie categories to accept or decline.
                    </p>
                  </div>
                </div>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Note</p>
                    <p className="text-sm">
                      Disabling essential cookies may affect how Radiance functions.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* 4. Third-Party Cookies */}
          <AccordionItem
            title="4. Third-Party Cookies"
            isOpen={openSections.thirdParty}
            onToggle={() => toggleSection('thirdParty')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Some trusted third-party services may place their own cookies when you visit Radiance or click a link. These providers have their own privacy and cookie policies.
              </p>
              
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Analytics Tools</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Google Analytics</li>
                    <li>• Hotjar</li>
                    <li>• Mixpanel</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Social & Advertising Tools</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Meta (Facebook) Pixel</li>
                    <li>• Google Ads / Tag Manager</li>
                    <li>• Pinterest Tag</li>
                    <li>• TikTok Pixel</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Affiliate Networks</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Affiliate programs may place cookies to confirm a purchase came from Radiance. Examples include:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Amazon Associates</li>
                    <li>• Sephora affiliate program partners</li>
                    <li>• Ulta Beauty affiliate partners</li>
                    <li>• Additional brand-specific programs</li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-2 italic">
                    These cookies contain no personal identifiers.
                  </p>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* 5. Your Rights */}
          <AccordionItem
            title="5. Your Rights"
            isOpen={openSections.rights}
            onToggle={() => toggleSection('rights')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Depending on your location (PIPEDA, GDPR, CCPA/CPRA), you may have rights such as:
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Eye className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Access</h4>
                    <p className="text-sm text-gray-600">Request a copy of data we hold about you</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Database className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Deletion</h4>
                    <p className="text-sm text-gray-600">Ask us to delete personal data</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Edit className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Correction</h4>
                    <p className="text-sm text-gray-600">Fix inaccurate information</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Settings className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Opt-Out</h4>
                    <p className="text-sm text-gray-600">Decline non-essential cookies anytime via browser tools</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Target className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Information</h4>
                    <p className="text-sm text-gray-600">Ask how cookies and data are used</p>
                  </div>
                </div>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">To exercise these rights, contact us at:</p>
                    <p className="text-sm">
                      📩 <a href="mailto:hello@meetradiance.com" className="text-[#ab3d22] hover:underline">hello@meetradiance.com</a>
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* 6. Contact Us */}
          <AccordionItem
            title="6. Contact Us"
            isOpen={openSections.contact}
            onToggle={() => toggleSection('contact')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Have questions about cookies or want help updating your preferences?
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-[#ab3d22]" />
                    <span className="text-sm font-medium">Privacy & Cookie Inquiries:</span>
                    <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline">
                      hello@meetradiance.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-[#ab3d22]" />
                    <span className="text-sm font-medium">Manage Cookies:</span>
                    <span className="text-sm text-gray-600">Via your browser settings or our cookie banner</span>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>
        </div>
      </Container>
      
      <Footer />
    </div>
  );
};

export default CookiesPage;
